import { useEffect, useState } from "react";
import { getRoadmap, updateTopic } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Lock, CheckCircle2, Circle, Sparkles, PlayCircle, Pencil, Plus } from "lucide-react";
import TopicDialog from "@/components/TopicDialog";
import { toast } from "sonner";

const STATUS_STYLES = {
  locked: "opacity-40 border-dashed",
  unlocked: "border-border",
  in_progress: "border-accent",
  completed: "border-primary",
  mastered: "border-primary shadow-[0_0_20px_rgba(57,211,83,0.25)]",
};

function StatusIcon({ status }) {
  if (status === "locked") return <Lock className="h-4 w-4 text-muted-foreground" />;
  if (status === "in_progress") return <PlayCircle className="h-4 w-4 text-accent" />;
  if (status === "completed") return <CheckCircle2 className="h-4 w-4 text-primary" />;
  if (status === "mastered") return <Sparkles className="h-4 w-4 text-primary" />;
  return <Circle className="h-4 w-4 text-muted-foreground" />;
}

export default function Roadmap() {
  const [levels, setLevels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [topicDialogOpen, setTopicDialogOpen] = useState(false);
  const [editingTopic, setEditingTopic] = useState(null);
  const [targetLevel, setTargetLevel] = useState(null);

  const refresh = async () => {
    setLoading(true);
    const data = await getRoadmap();
    setLevels(data.levels || []);
    setLoading(false);
  };

  useEffect(() => { refresh(); }, []);

  const setStatus = async (topic, next) => {
    if (topic.status === "locked") return;
    await updateTopic(topic.id, next);
    toast.success(next === "mastered" ? "Mastered +100 XP" : next === "completed" ? "Completed +50 XP" : "Updated");
    refresh();
  };

  const openAddTopic = (level) => { setEditingTopic(null); setTargetLevel(level); setTopicDialogOpen(true); };
  const openEditTopic = (topic, level) => { setEditingTopic(topic); setTargetLevel(level); setTopicDialogOpen(true); };

  if (loading) {
    return <div className="mono text-sm text-muted-foreground dot-pulse">Loading roadmap…</div>;
  }

  return (
    <div className="space-y-8">
      <div>
        <p className="overline">Learning Roadmap</p>
        <h1 className="text-4xl md:text-5xl font-bold tracking-tight mt-2">
          <span className="text-primary terminal-glow">$</span> Skill tree, not calendar.
        </h1>
        <p className="text-muted-foreground mt-2 max-w-2xl">
          Chase mastery, not dates. Finish faster → unlock the next. Struggle → revision schedules itself.
        </p>
      </div>

      <div className="space-y-10">
        {levels.map((lv) => (
          <div key={lv.level}>
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-4">
              <div>
                <span className="overline">Level {lv.level}</span>
                <h2 className="text-2xl font-bold mt-1">{lv.name}</h2>
                <p className="text-sm text-muted-foreground">{lv.tagline}</p>
              </div>
              <div className="flex items-center gap-3 sm:gap-4">
                <div className="w-full sm:w-48 space-y-1">
                  <div className="flex justify-between mono text-xs">
                    <span className="text-muted-foreground">Progress</span>
                    <span className="text-primary">{lv.completion_pct}%</span>
                  </div>
                  <Progress value={lv.completion_pct} className="h-1.5 bg-muted [&>div]:bg-primary" />
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  data-testid={`level-add-topic-${lv.level}`}
                  onClick={() => openAddTopic(lv.level)}
                  className="border-border mono text-[11px] shrink-0"
                >
                  <Plus className="h-3 w-3 mr-1" /> Add Topic
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {lv.topics.map((t) => (
                <Card
                  key={t.id}
                  data-testid={`topic-card-${t.id}`}
                  className={`bg-card rounded-md border transition-colors duration-150 ${STATUS_STYLES[t.status] || "border-border"}`}
                >
                  <CardContent className="p-5 space-y-3">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-2">
                        <StatusIcon status={t.status} />
                        <h3 className="mono text-base font-semibold">{t.name}</h3>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="mono text-[10px] border-border">
                          {t.est_hours}h · L{t.difficulty}/5
                        </Badge>
                        <button
                          data-testid={`topic-edit-${t.id}`}
                          onClick={() => openEditTopic(t, lv.level)}
                          className="text-muted-foreground hover:text-foreground"
                        >
                          <Pencil className="h-3 w-3" />
                        </button>
                      </div>
                    </div>
                    <p className="text-sm text-muted-foreground">{t.description}</p>
                    <div className="text-xs text-foreground/80 border-l-2 border-primary/60 pl-3 mono">
                      <span className="text-primary">◆</span> {t.assignment}
                    </div>
                    <div className="flex flex-wrap gap-2 pt-1">
                      {t.status === "unlocked" && (
                        <Button data-testid={`topic-start-${t.id}`} size="sm" variant="outline" className="border-border mono text-[11px]" onClick={() => setStatus(t, "in_progress")}>Start</Button>
                      )}
                      {t.status === "in_progress" && (
                        <Button data-testid={`topic-complete-${t.id}`} size="sm" className="bg-primary text-primary-foreground hover:bg-secondary hover:text-white mono text-[11px]" onClick={() => setStatus(t, "completed")}>Mark Complete</Button>
                      )}
                      {t.status === "completed" && (
                        <Button data-testid={`topic-master-${t.id}`} size="sm" variant="outline" className="border-primary text-primary hover:bg-primary hover:text-primary-foreground mono text-[11px]" onClick={() => setStatus(t, "mastered")}>Master</Button>
                      )}
                      {t.status === "locked" && (
                        <span className="mono text-[10px] text-muted-foreground">Locked — complete prior topic to unlock</span>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        ))}
      </div>

      <TopicDialog
        open={topicDialogOpen}
        onOpenChange={setTopicDialogOpen}
        topic={editingTopic}
        level={targetLevel}
        onSaved={refresh}
      />
    </div>
  );
}
